import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Slice } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { CellLabel, EntityListSection, EntityValuePillList, useLang, usePageEntity } from '@shapeable/ui';
import { Region } from '@shapeable/kelp-forest-alliance-types';

const cls = classNames('slice-region-data');

// -------- Types -------->

// -------- Props -------->

export type SliceRegionDataProps = Classable & HasChildren & { 
  entity?: Slice;
};

export const SliceRegionDataDefaultProps: Omit<SliceRegionDataProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(3)};
  `,
});

const LabelStyles = breakpoints({
  base: css`
    font-size: 1em;
    color: ${theme.COLOR('mid')};
  `,
});

const ResultStyles = breakpoints({
  base: css`
    font-weight: 500;
    font-size: 1em;
  `,
});

const MetricStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.UNIT(2)};
    padding-top: ${theme.UNIT(3)};
    ${theme.H_DOT_BG('line-subtle')};
  `,
});

const PopulationNearKelpStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});

const CoastlineLengthStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});

const OceanWarmingRateStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});

const GdpWithin50kmKelpStyles = breakpoints({
  base: css`
    ${MetricStyles}
  `,
});

const KelpAreaHectaresStyles = breakpoints({
  base: css`   
    ${MetricStyles}
  `,
});

const AudienceReachedStyles = breakpoints({
  base: css`    
    ${MetricStyles}
  `,
});

const KeySpeciesStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(3)};
    ${theme.H_DOT_BG('line-subtle')};
  `,
});

const SpeciesStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(3)};
  `,
});


// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Label: styled(CellLabel)`${LabelStyles}`,
    Result: styled.span`${ResultStyles}`,

    PopulationNearKelp: styled.div`${PopulationNearKelpStyles}`,
    CoastlineLength: styled.div`${CoastlineLengthStyles}`,
    OceanWarmingRate: styled.div`${OceanWarmingRateStyles}`,
    GdpWithin50kmKelp: styled.div`${GdpWithin50kmKelpStyles}`,
    KelpAreaHectares: styled.div`${KelpAreaHectaresStyles}`,
    AudienceReached: styled.div`${AudienceReachedStyles}`,
    KeySpecies: styled.div`${KeySpeciesStyles}`,
      Species: styled(EntityValuePillList)`${SpeciesStyles}`,
};

export const SliceRegionData: Shapeable.FC<SliceRegionDataProps> = (props) => {
  const { className, children, entity } = props;

  const activeEntity = usePageEntity() as Region;

  const t = useLang();

  const { populationNearKelp, coastlineLengthkm, oceanWarmingRate, 
    kelpAreaHectares, gdpWithin50kmKelp, species = [] } = activeEntity || {};
  
  return (
    <My.Container className={cls.name(className)}>
      {
        !!populationNearKelp &&
        <My.PopulationNearKelp>
          <My.Label>{t('People living within 50km of kelp forests:')}</My.Label>
          <My.Result>{populationNearKelp?.toLocaleString()}</My.Result>
        </My.PopulationNearKelp>
      }
      {
        !!coastlineLengthkm &&
        <My.CoastlineLength>
          <My.Label>{t('Coastline (KM):')}</My.Label>
          <My.Result>{coastlineLengthkm?.toLocaleString()} miles</My.Result>
        </My.CoastlineLength>
      }
      {
        !!gdpWithin50kmKelp &&
        <My.GdpWithin50kmKelp>
          <My.Label>{t('GDP(B) within 50KM of Kelp:')}</My.Label>
          <My.Result>${gdpWithin50kmKelp?.toLocaleString()} billion</My.Result>
        </My.GdpWithin50kmKelp>
      }
      {
        !!oceanWarmingRate &&
        <My.OceanWarmingRate>
          <My.Label>{t('Ocean Warming Rate by 2100 (°C):')}</My.Label>
          <My.Result>{oceanWarmingRate?.toLocaleString()} °C</My.Result>
        </My.OceanWarmingRate>
      }
      {
        !!kelpAreaHectares &&
        <My.KelpAreaHectares>
          <My.Label>{t('KM2 of Kelp:')}</My.Label>
          <My.Result>{kelpAreaHectares?.toLocaleString()}</My.Result>
        </My.KelpAreaHectares>
      }
      {
        !!species.length && 
        <My.KeySpecies>
          <My.Label>{t('Key Species:')}</My.Label>
          <My.Species
            showLabel={false}
            items={species}
          />
        </My.KeySpecies>
        
      }
      
    </My.Container>
  )
};

SliceRegionData.cls = cls;